/**
 * StreamDeckButton — fires a real, already-configured Elgato Stream Deck
 * action (OBS scene, Hue, hotkey — whatever sits on that key).
 *
 * REWIRED 2026-07-28. The previous version was dead end-to-end in three
 * independent ways, and it is worth naming them because the shape of this
 * component is a direct response to each:
 *
 *   1. It gated on `props.connected()`, the legacy Node-bridge `open`
 *      signal, which is never set (`bridge.connect()` only runs under
 *      `VITE_LEGACY_BRIDGE=1`). The button shipped permanently disabled.
 *   2. It sent `sd.press` to that dead bridge.
 *   3. The bridge-side client STUBBED the press: it logged and resolved
 *      SUCCESS, so had the first two been fixed, the button would have
 *      flashed a green "ack" while doing nothing at all.
 *
 * (3) is the one that shapes this file. A control that lies about firing is
 * worse than one that visibly cannot, so every failure here is surfaced:
 * unassigned, no bridge, and dispatch error each get their own state rather
 * than collapsing into a hopeful flash.
 *
 * Path: `executeStreamDeckAction()` → OPBridge `POST /streamdeck/execute`
 * → local `@elgato/mcp-server` → named pipe → Stream Deck app. Routing
 * through OPBridge is forced, not stylistic: the MCP server 500s on any
 * request carrying an `Origin` header, so the iPad browser cannot call it.
 */

import { createSignal } from 'solid-js';
import { executeStreamDeckAction, fanoutTargets } from '../opbridge/dispatch.ts';
import { anyBridgeConnectedReactive } from '../opbridge/store.ts';
import { findStreamDeckAction } from '../opbridge/streamDeckActions.ts';
import type { StreamDeckItem } from '../layout/store.ts';
import { editOwnsGesture, selectedItemId } from '../layout/store.ts';

interface StreamDeckButtonProps {
  item: StreamDeckItem;
}

/* The `data-color` attribute and its COLOR_TOKEN map were REMOVED 2026-07-30,
 * because together with the CSS they were the cause of "Stream Deck buttons are
 * locked yellow". The map only had keys for the four retired LayoutColors, so
 * any item created after DEC-127 (which replaced `color` with `hue`) had
 * `props.item.color === undefined` -> `COLOR_TOKEN[undefined]` -> no
 * `data-color` attribute -> none of the four `[data-color=...]` CSS rules
 * matched -> the element fell through to `.sd-item-btn`'s BASE declarations,
 * which hardcoded --color-armed for background, border AND text. Amber base +
 * unreachable overrides = permanently yellow, and no picker could change it.
 * Colour now arrives the way it does on every other item: --hue on
 * `.editable-item`, washed over the skin's material. */

type FlashState = 'idle' | 'ok' | 'err';

export function StreamDeckButton(props: StreamDeckButtonProps) {
  const [pressed, setPressed] = createSignal(false);
  const [flash, setFlash] = createSignal<FlashState>('idle');
  const [errMsg, setErrMsg] = createSignal('');

  const assigned = () => !!props.item.actionId;
  const bridgeUp = () => anyBridgeConnectedReactive(props.item.bridgeIds);
  const canFire = () => assigned() && bridgeUp();

  /** Prefer the live catalogue title (the operator may have renamed the key
   *  on the deck since it was picked); fall back to the title cached on the
   *  item so the face still reads correctly with OPBridge down. */
  const liveTitle = () => {
    const a = findStreamDeckAction(props.item.actionId);
    return a?.title?.trim() || a?.name?.trim() || '';
  };
  const label = () =>
    props.item.label?.trim()
    || liveTitle()
    || props.item.actionTitle?.trim()
    || (assigned() ? 'Unknown action' : 'Unassigned');

  const name = () => props.item.name ?? '';
  const showName = () => !props.item.hideName;
  const showLabel = () => !props.item.hideLabel;

  /** One-word state for the status line + CSS. */
  const state = (): 'unassigned' | 'offline' | 'ready' => {
    if (!assigned()) return 'unassigned';
    if (!bridgeUp()) return 'offline';
    return 'ready';
  };

  const statusText = () => {
    if (flash() === 'err') return errMsg();
    if (flash() === 'ok') return 'fired';
    switch (state()) {
      case 'unassigned': return 'pick action';
      case 'offline':    return 'no bridge';
      default:           return 'stream deck';
    }
  };

  async function fire() {
    if (!canFire()) return;
    setPressed(true);
    setTimeout(() => setPressed(false), 120);

    // Fan out to every configured bridge (multi-select, 2026-08-13) —
    // undefined bridgeIds resolves to the default bridge, since the MCP
    // server that actually holds the physical Stream Deck may be on a
    // specific machine.
    const results = (await Promise.all(
      fanoutTargets(props.item.bridgeIds).map((target) => executeStreamDeckAction(props.item.actionId!, target)),
    )).flat();
    const ok = results.some((r) => r.kind === 'ok');
    if (ok) {
      setFlash('ok');
      setErrMsg('');
      setTimeout(() => setFlash('idle'), 400);
    } else {
      const first = results[0];
      setFlash('err');
      // Surface the actual reason. "It didn't work" with no cause is what
      // sent the previous version undiagnosed for a year.
      setErrMsg(first ? (first.detail ? `${first.kind}: ${first.detail}` : first.kind) : 'failed');
      setTimeout(() => setFlash('idle'), 2000);
    }
  }

  return (
    <div class="sd-item">
      <button
        class="sd-item-btn"
        type="button"
        data-pressed={pressed() ? 'true' : 'false'}
        data-flash={flash()}
        data-state={state()}
        onPointerDown={() => {
          // Yield to the editor exactly as FireButton does: in edit mode a
          // tap SELECTS the item, and on a selected item it is the
          // right-click escape hatch. Without this, selecting a Stream Deck
          // item to edit it fired its Elgato action (OBS, a hotkey) - found
          // 2026-09-29 by mod_share_every_kind's Inspector clicks.
          if (editOwnsGesture(props.item.id)) return;
          if (selectedItemId() === props.item.id) return;
          void fire();
        }}
        disabled={!canFire()}
        title={
          !assigned()
            ? 'No Stream Deck action assigned - pick one in the Inspector'
            : !bridgeUp()
              ? 'No OPBridge connected'
              : `Fire "${label()}" on the Stream Deck`
        }
        aria-label={assigned() ? `Fire Stream Deck action ${label()}` : 'Unassigned Stream Deck button'}
      >
        <span class="sd-item-key">SD</span>
        {showLabel() && <span class="sd-item-label">{label()}</span>}
        {showName() && name() && <span class="sd-item-name">{name()}</span>}
      </button>
      <div class="sd-item-status" data-state={flash() === 'idle' ? state() : flash()}>
        {statusText()}
      </div>
    </div>
  );
}
